import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxEditorModule, Editor } from 'ngx-editor';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { CONTACT_TOPICS } from '../shared/contact-topics';
import { SafeHtmlPipe } from '../shared/safe-html.pipe';

export interface ContactMessageDto {
  id: number;
  userId: number | null;
  userName: string;
  recipientUserId?: number | null;
  recipientUserName?: string;
  parentId?: number | null;
  topic: string;
  messageText: string;
  createdAt: string;
  readAt: string | null;
  hasAttachment?: boolean;
  attachmentFilename?: string | null;
}

@Component({
  selector: 'app-messenger',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, NgxEditorModule, SafeHtmlPipe],
  templateUrl: './messenger.component.html',
  styleUrl: './messenger.component.css'
})
export class MessengerComponent implements OnInit, OnDestroy {
  sessionId = '';
  loading = false;
  error = '';
  messages: ContactMessageDto[] = [];
  threadMessages: ContactMessageDto[] = [];
  selectedLoading = false;

  composeModal = false;
  composeMode: 'new' | 'reply' = 'new';
  composeCancelConfirm = false;
  composeSendConfirm = false;
  composeModalTop = 80;
  composeModalLeft = 100;
  private composeDragStart = { x: 0, y: 0, top: 0, left: 0 };
  private composeDragMoveBound?: (e: MouseEvent) => void;
  private composeDragUpBound?: () => void;

  sortColumn = 'createdAt';
  sortDir: 'asc' | 'desc' = 'desc';
  inboxView: 'inbox' | 'sent' = 'inbox';
  page = 0;
  pageSize = 100;
  totalElements = 0;
  totalPages = 0;

  newMessageTopic = '';
  newMessageHtml = '';
  newMessageAttachment: File | null = null;
  newMessageSending = false;
  newMessageSendError = '';
  newMessageEditor: Editor | null = null;
  readonly topics = CONTACT_TOPICS;

  maxDailyUserMessages = 10;
  messagesSentToday = 0;
  private refreshSub?: ReturnType<typeof setInterval>;

  constructor(
    private authService: AuthService,
    private restService: RestService
  ) {}

  get canSendNewMessage(): boolean {
    return this.messagesSentToday < this.maxDailyUserMessages;
  }

  get dailyLimitMessage(): string {
    return `Daily limit reached (${this.maxDailyUserMessages} messages per day). You can send more tomorrow.`;
  }

  ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    this.loadContactConfig();
    this.loadMessages();
    this.refreshSub = setInterval(() => {
      if (this.sessionId && !this.loading && !this.composeModal) {
        this.loadMessagesSilent();
        this.loadContactConfig();
      }
    }, 30000);
  }

  ngOnDestroy() {
    if (this.refreshSub) clearInterval(this.refreshSub);
    if (this.newMessageEditor) {
      this.newMessageEditor.destroy();
      this.newMessageEditor = null;
    }
  }

  async loadContactConfig() {
    const cfg = await this.restService.getContactConfig(this.sessionId);
    if (cfg) {
      this.maxDailyUserMessages = cfg.maxDailyUserMessages ?? 10;
      this.messagesSentToday = cfg.messagesSentToday ?? 0;
    }
  }

  get sortedMessages(): ContactMessageDto[] {
    const arr = [...this.messages];
    const mult = this.sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (this.sortColumn) {
        case 'topic': va = (a.topic || '').toLowerCase(); vb = (b.topic || '').toLowerCase(); break;
        case 'createdAt': va = a.createdAt || ''; vb = b.createdAt || ''; break;
        default: va = a.createdAt || ''; vb = b.createdAt || '';
      }
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
    return arr;
  }

  async loadMessages() {
    if (!this.sessionId) return;
    this.loading = true;
    this.error = '';
    try {
      const sort = `${this.sortColumn},${this.sortDir}`;
      const res = await this.restService.getContactMessages(this.sessionId, this.page, this.pageSize, sort, this.inboxView === 'inbox');
      this.messages = res.content || [];
      this.totalElements = res.totalElements ?? 0;
      this.totalPages = res.totalPages ?? 0;
    } catch (e: any) {
      this.error = e?.message || 'Failed to load messages';
    } finally {
      this.loading = false;
    }
  }

  private async loadMessagesSilent() {
    if (!this.sessionId) return;
    try {
      const sort = `${this.sortColumn},${this.sortDir}`;
      const res = await this.restService.getContactMessages(this.sessionId, this.page, this.pageSize, sort, this.inboxView === 'inbox');
      this.messages = res.content || [];
      this.totalElements = res.totalElements ?? 0;
      this.totalPages = res.totalPages ?? 0;
      if (this.threadMessages.length > 0) {
        const thread = await this.restService.getContactMessageThread(this.sessionId, this.threadMessages[0].id);
        if (thread?.length) this.threadMessages = thread;
      }
    } catch {
      // ignore
    }
  }

  setInboxView(view: 'inbox' | 'sent') {
    this.inboxView = view;
    this.page = 0;
    this.loadMessages();
  }

  goToPage(p: number) {
    if (p < 0 || p >= this.totalPages) return;
    this.page = p;
    this.loadMessages();
  }

  setSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = 'desc';
    }
    this.page = 0;
    this.loadMessages();
  }

  isInThread(msgId: number): boolean {
    return this.threadMessages.some(tm => tm.id === msgId);
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi-chevron-expand';
    return this.sortDir === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
  }

  formatDate(s: string | null): string {
    if (!s) return '—';
    try {
      const d = new Date(s);
      return isNaN(d.getTime()) ? s : d.toLocaleString();
    } catch {
      return s;
    }
  }

  async selectMessage(msg: ContactMessageDto) {
    this.threadMessages = [];
    this.selectedLoading = true;
    try {
      const thread = await this.restService.getContactMessageThread(this.sessionId, msg.id);
      this.threadMessages = thread || [];
    } catch (e: any) {
      this.error = e?.message || 'Failed to load thread';
    } finally {
      this.selectedLoading = false;
    }
  }

  closeDetail() {
    this.threadMessages = [];
  }

  onComposeDragStart(e: MouseEvent) {
    if ((e.target as HTMLElement).closest('.btn-close')) return;
    this.composeDragStart = { x: e.clientX, y: e.clientY, top: this.composeModalTop, left: this.composeModalLeft };
    this.composeDragMoveBound = (ev: MouseEvent) => {
      this.composeModalTop = this.composeDragStart.top + ev.clientY - this.composeDragStart.y;
      this.composeModalLeft = this.composeDragStart.left + ev.clientX - this.composeDragStart.x;
    };
    this.composeDragUpBound = () => {
      document.removeEventListener('mousemove', this.composeDragMoveBound!);
      document.removeEventListener('mouseup', this.composeDragUpBound!);
    };
    document.addEventListener('mousemove', this.composeDragMoveBound);
    document.addEventListener('mouseup', this.composeDragUpBound);
  }

  resetComposePosition() {
    this.composeModalTop = 80;
    this.composeModalLeft = 100;
  }

  openNewMessage() {
    this.composeMode = 'new';
    this.composeModal = true;
    this.newMessageTopic = '';
    this.newMessageHtml = '';
    this.newMessageAttachment = null;
    this.composeSendConfirm = false;
    this.composeCancelConfirm = false;
    this.newMessageEditor = new Editor();
    this.resetComposePosition();
  }

  openReply() {
    if (!this.threadMessages.length) return;
    this.composeMode = 'reply';
    this.composeModal = true;
    const last = this.threadMessages[this.threadMessages.length - 1];
    this.newMessageTopic = last.topic?.startsWith('Re: ') ? last.topic : 'Re: ' + (last.topic || '');
    this.newMessageHtml = '';
    this.newMessageAttachment = null;
    this.composeSendConfirm = false;
    this.composeCancelConfirm = false;
    this.newMessageEditor = new Editor();
    this.resetComposePosition();
  }

  requestCloseCompose() {
    const hasBody = !this.isHtmlEmpty(this.newMessageHtml);
    const hasContent = this.composeMode === 'reply'
      ? (hasBody || !!this.newMessageAttachment)
      : (hasBody || !!this.newMessageTopic?.trim() || !!this.newMessageAttachment);
    if (hasContent) {
      this.composeCancelConfirm = true;
    } else {
      this.closeCompose();
    }
  }

  /** HTML editor may leave empty tags like <p></p> when content is deleted */
  private isHtmlEmpty(html: string): boolean {
    if (!html?.trim()) return true;
    const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
    return !text;
  }

  cancelComposeConfirmClose() {
    this.composeCancelConfirm = false;
  }

  confirmCloseCompose() {
    this.composeCancelConfirm = false;
    this.closeCompose();
  }

  closeCompose() {
    this.composeModal = false;
    this.composeSendConfirm = false;
    this.newMessageSendError = '';
    if (this.newMessageEditor) {
      this.newMessageEditor.destroy();
      this.newMessageEditor = null;
    }
  }

  get canSendCompose(): boolean {
    if (this.composeMode === 'new') {
      return !!this.newMessageTopic?.trim() && !!this.newMessageHtml?.trim() && this.canSendNewMessage;
    }
    return !!this.newMessageHtml?.trim() && this.canSendNewMessage;
  }

  showComposeSendConfirm() {
    if (!this.canSendCompose) return;
    this.newMessageSendError = '';
    this.composeSendConfirm = true;
  }

  cancelComposeSend() {
    this.composeSendConfirm = false;
    this.newMessageSendError = '';
  }

  async confirmSendCompose() {
    if (!this.canSendCompose || !this.sessionId) return;
    this.newMessageSending = true;
    this.newMessageSendError = '';
    try {
      if (this.composeMode === 'reply') {
        const replyToId = this.threadMessages[this.threadMessages.length - 1].id;
        const created = await this.restService.replyContactMessage(
          this.sessionId,
          replyToId,
          this.newMessageHtml.trim(),
          this.newMessageAttachment ?? undefined
        );
        this.threadMessages.push(created);
        if (this.inboxView === 'sent') this.messages.unshift(created);
      } else {
        await this.restService.submitContact(this.sessionId, {
          topic: this.newMessageTopic.trim(),
          messageText: this.newMessageHtml.trim()
        });
        await this.loadContactConfig();
        if (this.inboxView === 'sent') {
          await this.loadMessages();
        }
      }
      this.composeSendConfirm = false;
      this.closeCompose();
      await this.loadContactConfig();
    } catch (err: any) {
      this.newMessageSendError = err?.error?.error || err?.message || 'Failed to send. Please try again.';
    } finally {
      this.newMessageSending = false;
    }
  }

  onAttachmentChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.newMessageAttachment = input.files?.[0] ?? null;
  }

  async downloadAttachment(msg: ContactMessageDto) {
    if (!this.sessionId || !msg.hasAttachment) return;
    try {
      const blob = await this.restService.getContactMessageAttachment(this.sessionId, msg.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = msg.attachmentFilename || 'attachment';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      this.error = e?.message || 'Failed to download attachment';
    }
  }
}
